import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface BrandingSettings {
  id: string;
  app_name: string;
  logo_url?: string;
  primary_color: string;
  updated_at: string;
}

export const DEFAULT_BRANDING = {
  app_name: "ZKI LMS",
  primary_color: "#2563eb",
};

/** Lightness (%) for each shade, holding hue/saturation constant — 600 is
 * overridden with the exact input color below so the picked color always
 * shows up verbatim on primary buttons/links. */
const SHADE_LIGHTNESS: Record<number, number> = {
  50: 97,
  100: 93,
  200: 86,
  300: 76,
  400: 65,
  500: 58,
  600: 50,
  700: 40,
  800: 33,
  900: 27,
};

export const BRAND_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/** Derives a full 50-900 tint/shade scale from one base hex color. Not a
 * color-science-grade ramp — just enough to keep the rest of the palette
 * (hover states, badges, soft backgrounds) visually consistent with
 * whatever primary color an admin picks. */
export function generateBrandShades(hex: string): Record<number, string> {
  const { h, s } = hexToHsl(hex);
  const shades: Record<number, string> = {};
  for (const shade of BRAND_SHADES) {
    shades[shade] = hslToHex(h, s, SHADE_LIGHTNESS[shade]);
  }
  shades[600] = normalizeHex(hex);
  return shades;
}

/** Applies a primary color live by overriding the brand-* CSS custom
 * properties on the document root. Tailwind v4's generated utilities read
 * colors via var(--color-brand-*), so every existing brand-500/600/etc.
 * class across the app picks up the change immediately — no rebuild. */
export function applyBrandColor(hex: string) {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
  const shades = generateBrandShades(hex);
  const root = document.documentElement.style;
  for (const shade of BRAND_SHADES) {
    root.setProperty(`--color-brand-${shade}`, shades[shade]);
  }
}

/** Fetches branding once (cached) and keeps the live theme color in sync
 * with it. Call near the app root. */
export function useBranding() {
  const { data } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<BrandingSettings>("/api/v1.0/settings"),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    applyBrandColor(data?.primary_color || DEFAULT_BRANDING.primary_color);
  }, [data?.primary_color]);

  return data;
}

function normalizeHex(hex: string): string {
  return hex.startsWith("#") ? hex : `#${hex}`;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const clean = normalizeHex(hex).slice(1);
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / d) % 6;
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  const sN = s / 100;
  const lN = l / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lN - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
